import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AssessmentOptionsModal from './AssessmentOptionsModal';
import { useFamilyContext } from '../../contexts/FamilyContext';
import AssessmentDataService from '../../services/AssessmentDataService';

/**
 * Assessment Entry Point - handles the decision flow for starting assessments
 * Shows modal with three options: Regenerate, Retake, Revise
 * Automatically navigates to appropriate flow based on user choice
 */
const AssessmentEntry = () => {
  const navigate = useNavigate();
  const { currentUser } = useFamilyContext();
  const [showModal, setShowModal] = useState(true);
  const [hasCheckedExisting, setHasCheckedExisting] = useState(false);

  // If user has no existing assessments, skip modal and go straight to fresh assessment
  useEffect(() => {
    const checkForDirectNavigation = async () => {
      if (!currentUser?.id) return;

      try {
        const hasExisting = await AssessmentDataService.hasExistingAssessments(currentUser.id);
        
        if (!hasExisting) {
          // No existing assessments, go straight to fresh assessment
          console.log('No existing assessments found, navigating to fresh assessment');
          navigate('/college/strategic-assessment', { 
            replace: true,
            state: { freshStart: true } 
          });
          return;
        }

        setHasCheckedExisting(true);
      } catch (error) {
        console.error('Error checking for existing assessments:', error);
        // On error, show the modal anyway
        setHasCheckedExisting(true);
      }
    };

    checkForDirectNavigation();
  }, [currentUser?.id, navigate]);

  const handleModalClose = () => {
    setShowModal(false);
    // Navigate back to college journey if modal is closed without selection
    navigate('/college');
  };

  // Don't render anything until we've checked for existing assessments
  if (!hasCheckedExisting) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-50 to-teal-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-azure-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Checking your assessment history...</p>
        </div>
      </div>
    );
  }

  return (
    <AssessmentOptionsModal 
      isOpen={showModal} 
      onClose={handleModalClose}
    />
  );
};

export default AssessmentEntry;